/* eslint-disable @typescript-eslint/no-explicit-any */

import './index.scss';
import { memo, useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';

import FetchingSpinner from 'components/fetching-spinner';

const apiInstance = axios.create({
  withCredentials: false,
});

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const fetchPDFData = (url: string, signal?: AbortSignal | undefined) => {
  return apiInstance({
    method: 'GET',
    url,
    signal,
    responseType: 'blob',
  });
};

const transformResponse = (response: { data: any }) => {
  return window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
};

const useGetPDFData = (url: string, id: string) => {
  const { data, isPending, error } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['aws-report', id],
    queryFn: ({ signal }) => fetchPDFData(url, signal),
    select: transformResponse,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return { data, isPending, error };
};

const Viewer = ({ file, id }: { file: string; id: string }) => {
  const [numPages, setNumPages] = useState<number>();
  const { data, isPending } = useGetPDFData(file, id);

  const onDocumentLoadSuccess = useCallback(({ numPages: nextNumPages }: any) => {
    setNumPages(nextNumPages);
  }, []);

  if (isPending) return <FetchingSpinner />;

  return (
    <div className="docContainer">
      <Document loading={<FetchingSpinner />} file={data} onLoadSuccess={onDocumentLoadSuccess}>
        {Array.from(new Array(numPages), (el, index) => (
          <Page
            renderAnnotationLayer={false}
            renderTextLayer={false}
            key={`page-${id}_${index + 1}`}
            pageNumber={index + 1}
          />
        ))}
      </Document>
    </div>
  );
};

const PdfViewer = memo(Viewer);

export default PdfViewer;
